import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { OVERLAY_BASE_Z, acquireOverlayLevel, overlayZ, releaseOverlayLevel } from "../../lib/overlay";

// Lock body scroll while a modal/sheet is mounted. Ref-counted so nested overlays don't unlock early.
// v2 — uses the position:fixed body technique instead of `overflow:hidden`: on iOS/Safari
// `overflow:hidden` doesn't actually stop scrolling and leaves position:fixed overlays anchored to
// the OLD scroll offset (so an opened modal appears scrolled up / with its header off-screen). Pinning
// the body at `top:-scrollY` keeps content in place AND makes fixed overlays sit correctly at the top.
let _scrollLocks = 0;
let _savedY = 0;
export function useScrollLock() {
  useEffect(() => {
    _scrollLocks += 1;
    if (_scrollLocks === 1) {
      _savedY = window.scrollY || window.pageYOffset || 0;
      const b = document.body;
      // v33b — už NEkompenzujeme šírku scrollbaru: `html { scrollbar-gutter: stable }` drží medzeru
      // rezervovanú stále, takže po zamknutí (body → position:fixed) scrollbar nezmizne a šírka
      // viewportu sa nemení. Pridávať paddingRight/--bz-sbw by teraz obsah naopak posunulo.
      b.style.position = "fixed";
      b.style.top = `-${_savedY}px`;
      b.style.left = "0";
      b.style.right = "0";
      b.style.width = "100%";
      b.style.overflow = "hidden";
    }
    // v29.3 — po návrate do appky zámok NEPOTVRDZUJEME (v29.0 áno — a to bola chyba: keby
    // počítadlo `_scrollLocks` z akéhokoľvek dôvodu uviazlo nad nulou, aktívne sme telu vracali
    // `position:fixed; overflow:hidden` a appka pôsobila zamrznuto). Namiesto toho ho LIEČIME:
    // keď je otvorených prekrytí nula a telo je stále zamknuté, zámok uvoľníme.
    const heal = () => {
      if (document.visibilityState !== "visible") return;
      const b = document.body;
      if (_scrollLocks === 0 && b.style.position === "fixed") {
        b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = "";
        b.style.width = ""; b.style.overflow = "";
      }
    };
    document.addEventListener("visibilitychange", heal);
    window.addEventListener("pageshow", heal);
    return () => {
      document.removeEventListener("visibilitychange", heal);
      window.removeEventListener("pageshow", heal);
      _scrollLocks = Math.max(0, _scrollLocks - 1);
      if (_scrollLocks === 0) {
        const b = document.body;
        b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = ""; b.style.overflow = "";
        window.scrollTo(0, _savedY);
      }
    };
  }, []);
}

// ── Vrstvenie prekrytí (z-index podľa hĺbky vnorenia) ──────────────────────
// f-batch #1 — `Modal` aj `Sheet` mali v CSS natvrdo `z-index: 300`, takže pri treťom vnorení
// (detail výzvy → chat → detail reakcií) rozhodovalo poradie uzlov v <body>, nie zámer, a vnorené
// prekrytie sa otváralo POD rodičom. Každé prekrytie si teraz pri mountnutí vypýta ďalšiu hladinu
// a pri unmountnutí ju vráti (spoločné počítadlo v `lib/overlay.ts` — jedno pre oba primitívy).
// `useLayoutEffect` = hladina je nastavená EŠTE PRED vykreslením, takže prekrytie ani na jeden
// snímok nepreblikne v zlej vrstve. Vracia hotový z-index do CSS premennej `--bz-overlay-z`.
export function useOverlayZ() {
  const [z, setZ] = useState(OVERLAY_BASE_Z);
  useLayoutEffect(() => {
    const level = acquireOverlayLevel();
    setZ(overlayZ(level));
    return () => releaseOverlayLevel(level);
  }, []);
  return z;
}

// ── Back button closes the top-most modal ──────────────────────────────────
// v32.23 — pressing the hardware/browser Back button while a Modal/Sheet is open used to fall
// through to the app's own back-override (tab-back / exit-confirm, see App.jsx) or, worse,
// navigate away entirely — the modal just sat there, unclosable via Back. Now every dialog
// built on the shared `Modal` (Modal.jsx) or `Sheet` (ds/Sheet.jsx) registers itself here while
// mounted (mounting = opening, unmounting = closing for both — callers always conditionally
// render them, e.g. `{show && <Modal onClose={...}>}`); App.jsx's popstate handler checks
// `hasOpenModal()`/`closeTopModal()` FIRST, before its tab-back / exit-confirm logic, so Back
// closes the top-most modal and leaves tab navigation untouched. Escape keeps working
// independently (each Modal/Sheet already has its own keydown listener).
let _modalStack = [];
export function hasOpenModal() { return _modalStack.length > 0; }
export function closeTopModal() {
  const top = _modalStack[_modalStack.length - 1];
  if (top) top.onClose();
}
export function useModalBackClose(onClose) {
  // stable ref so re-renders (new inline onClose each time) don't reshuffle stack order —
  // an entry's *position* is fixed at mount (= when it opened), only what it calls can update.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const entry = { onClose: () => onCloseRef.current && onCloseRef.current() };
    _modalStack.push(entry);
    return () => { _modalStack = _modalStack.filter((e) => e !== entry); };
  }, []);
}

// Long-press handlers (pointer events). `fired` ref is true when the press fired, so the click
// handler can be suppressed. The callback receives the pressed element (pointerdown's
// currentTarget) so callers can give per-element feedback (e.g. a brief lift).
export function useLongPress(cb, ms = 450) {
  const timer = useRef(null);
  const fired = useRef(false);
  const start = (e) => {
    fired.current = false;
    const el = e.currentTarget;
    timer.current = setTimeout(() => { fired.current = true; cb(el); }, ms);
  };
  const cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return {
    fired,
    handlers: {
      onPointerDown: start,
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onPointerMove: cancel,
      onContextMenu: (e) => e.preventDefault(),
    },
  };
}
